import React, { useState } from 'react';
import { Download, Monitor, Laptop, ShieldCheck, Mic, CheckCircle2, X, RefreshCw, AlertTriangle, ExternalLink } from 'lucide-react';
import Modal from './Modal';

export default function InstallAppModal({ onClose, onInstall, hasNativePrompt, isStandalone }) {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent);
  const [activeTab, setActiveTab] = useState(isMac ? 'mac' : 'windows');

  return (
    <Modal title="Install Physicians Health Portal on Your Computer" onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #0f766e, #115e59)',
          borderRadius: '12px',
          padding: '20px 24px',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          boxShadow: '0 4px 14px rgba(15, 118, 110, 0.25)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <img src="/icon-192.png" alt="PHP Logo" style={{ width: 56, height: 56, borderRadius: 12, background: '#ffffff', padding: 3, boxShadow: '0 4px 10px rgba(0,0,0,0.18)', flexShrink: 0 }} />
            <div style={{ maxWidth: '380px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#99f6e4', marginBottom: '4px' }}>
                OFFICIAL DESKTOP WEB APPLICATION
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', margin: '0 0 4px 0', color: '#ffffff' }}>
                Launch PHP from Your Desktop or Dock
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#ccfbf1', lineHeight: '1.4' }}>
                Install without downloading bulky installers. Runs in a dedicated, distraction-free application window with full AI note dictation and HIPAA safeguards.
              </p>
            </div>
          </div>

          <div>
            {isStandalone ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.2)', padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600' }}>
                <CheckCircle2 size={18} color="#5eead4" />
                Already Installed & Active
              </div>
            ) : hasNativePrompt ? (
              <button
                className="button"
                style={{
                  background: '#ffffff',
                  color: '#0f766e',
                  fontWeight: '700',
                  fontSize: '14px',
                  padding: '12px 22px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer'
                }}
                onClick={onInstall}
              >
                <Download size={18} />
                Install Application Now
              </button>
            ) : (
              <div style={{ fontSize: '12px', background: 'rgba(0,0,0,0.2)', padding: '8px 14px', borderRadius: '6px', color: '#e6fffa' }}>
                Follow browser steps below to install
              </div>
            )}
          </div>
        </div>

        {/* OS Selector Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setActiveTab('windows')}
            style={{
              padding: '10px 16px',
              fontWeight: activeTab === 'windows' ? '700' : '500',
              color: activeTab === 'windows' ? '#0f766e' : '#64748b',
              borderBottom: activeTab === 'windows' ? '3px solid #0f766e' : '3px solid transparent',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <Monitor size={17} /> Windows 10 & 11 (Chrome / Edge)
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('mac')}
            style={{
              padding: '10px 16px',
              fontWeight: activeTab === 'mac' ? '700' : '500',
              color: activeTab === 'mac' ? '#0f766e' : '#64748b',
              borderBottom: activeTab === 'mac' ? '3px solid #0f766e' : '3px solid transparent',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <Laptop size={17} /> macOS (Chrome / Safari / Edge)
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'windows' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f766e', fontWeight: '700' }}>
                <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#ccfbf1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>1</span>
                Microsoft Edge Installation
              </div>
              <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                <li>Open <strong>php.bridgetechnologies.org</strong> in Edge.</li>
                <li>Look at the right side of the address bar for the <strong>App Available</strong> icon (<Download size={13} style={{ display: 'inline', verticalAlign: 'middle' }} />).</li>
                <li>Click <strong>Install</strong> or click <strong>Settings (···) &gt; Apps &gt; Install Physicians Health Portal</strong>.</li>
                <li>Choose "Pin to taskbar" and "Pin to Start Menu".</li>
              </ol>
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f766e', fontWeight: '700' }}>
                <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#ccfbf1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>2</span>
                Google Chrome Installation
              </div>
              <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                <li>Open <strong>php.bridgetechnologies.org</strong> in Chrome.</li>
                <li>Click the <strong>Install</strong> icon in the address bar (computer monitor with down arrow).</li>
                <li>Or click <strong>Menu (⋮) &gt; Save and share &gt; Install Physicians Health Portal...</strong></li>
                <li>Click <strong>Install</strong>. PHP will open in its own dedicated window.</li>
              </ol>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f766e', fontWeight: '700' }}>
                <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#ccfbf1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>1</span>
                Apple Safari (macOS Sonoma+)
              </div>
              <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                <li>Open <strong>php.bridgetechnologies.org</strong> in Safari.</li>
                <li>Click the <strong>Share</strong> button in the toolbar, or choose <strong>File</strong> in the top menu bar.</li>
                <li>Select <strong>Add to Dock...</strong></li>
                <li>Click <strong>Add</strong>. PHP appears directly on your macOS Dock and in Launchpad.</li>
              </ol>
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: '#ffffff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#0f766e', fontWeight: '700' }}>
                <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#ccfbf1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>2</span>
                Google Chrome & Microsoft Edge (Mac)
              </div>
              <ol style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                <li>Open <strong>php.bridgetechnologies.org</strong> in Chrome or Edge.</li>
                <li>Click the <strong>Install</strong> icon in the address bar.</li>
                <li>Or choose <strong>Install Physicians Health Portal</strong> from the browser menu.</li>
                <li>The app installs to your <strong>Applications &gt; Chrome Apps</strong> folder.</li>
              </ol>
            </div>
          </div>
        )}

        {/* Feature Highlights Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginTop: '6px' }}>
          <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#0f766e', fontSize: '13px', marginBottom: '4px' }}>
              <Mic size={16} /> Microphone Permissions
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: '#475569', lineHeight: '1.4' }}>
              When prompted, allow microphone access to use the AI Clinical Note Generator for real-time dictation.
            </p>
          </div>

          <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#0f766e', fontSize: '13px', marginBottom: '4px' }}>
              <RefreshCw size={16} /> Automatic Seamless Updates
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: '#475569', lineHeight: '1.4' }}>
              Updates apply automatically in the background. Never interrupts active notes or recordings.
            </p>
          </div>

          <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#0f766e', fontSize: '13px', marginBottom: '4px' }}>
              <ShieldCheck size={16} /> HIPAA Safeguards
            </div>
            <p style={{ margin: 0, fontSize: '12px', color: '#475569', lineHeight: '1.4' }}>
              Zero local disk caching of PHI. Always sign out when sharing workstations with other clinic personnel.
            </p>
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
          <button type="button" className="button" onClick={onClose} style={{ padding: '8px 18px', fontSize: '13px' }}>
            Close Guide
          </button>
          {hasNativePrompt && !isStandalone && (
            <button
              type="button"
              className="button primary"
              onClick={() => { onInstall(); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 20px', fontSize: '13px' }}
            >
              <Download size={15} /> Install Now
            </button>
          )}
        </div>

      </div>
    </Modal>
  );
}

